import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { TRACK_PERSON_MENTION_PAGE_VISIT } from 'api/mention/mutations';
import {
  TrackPersonMentionPageVisit,
  TrackPersonMentionPageVisitVariables,
} from 'api/mention/types/TrackPersonMentionPageVisit';
import { getPersonMentionAttribution } from 'helpers/personMention';

/**
 * Fires `trackPersonMentionPageVisit` once per unique attribution when the
 * user lands on a Post / Stream / Interview / claim-your-profile page via a
 * PersonMention email link (`?c=pm&mid=<uuid>[&aid=<uuid>]`).
 *
 * Mount on the relevant landing pages. Idempotent per `mentionId+requestId`
 * pair — rerenders and React StrictMode double-mount do not re-fire, but
 * client-side navigation to a different mention DOES fire again. Tracking
 * failures are swallowed: they must never break the landing page.
 */
export const usePersonMentionPageVisitTracking = (): void => {
  const router = useRouter();
  const lastFiredKeyRef = useRef<string | null>(null);
  const [trackPersonMentionPageVisit] = useMutation<
    TrackPersonMentionPageVisit,
    TrackPersonMentionPageVisitVariables
  >(TRACK_PERSON_MENTION_PAGE_VISIT, {
    // Tracking failures must not affect the landing page experience or
    // produce console noise in production.
    onError: () => undefined,
  });

  useEffect(() => {
    if (!router.isReady) return;

    const attribution = getPersonMentionAttribution(router.query);
    if (!attribution) return;

    const key = `${attribution.mentionId}:${attribution.requestId ?? ''}`;
    if (lastFiredKeyRef.current === key) return;
    lastFiredKeyRef.current = key;

    trackPersonMentionPageVisit({
      variables: {
        input: {
          mentionId: attribution.mentionId,
          ...(attribution.requestId
            ? { requestId: attribution.requestId }
            : {}),
        },
      },
    }).catch(() => {
      // Tracking failures must not affect the landing page experience.
    });
  }, [router.isReady, router.query, trackPersonMentionPageVisit]);
};
